import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { AuthService } from '@app/shared/services/auth.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectIndustriesDataService } from '@app/shared/services/project-industries-data.service';
import { ProjectParticipantsDataService } from '@app/shared/services/project-participants-data.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { TaskTypesDataService } from '@app/shared/services/task-types-data.service';
import { GroupRole } from '@app/shared/types/enums/group-role.enum';
import { ProjectTaskUserRole } from '@app/shared/types/enums/project-task-user-role.enum';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TASK_PRIORITY_TRANSLATION_KEYS, TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TASK_SORT_FIELD_TRANSLATION_KEYS, TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { TASK_STATUS_TRANSLATION_KEYS, TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { Group } from '@app/shared/types/models/group/group.model';
import { Location } from '@app/shared/types/models/location/location.model';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';
import { Project } from '@app/shared/types/models/project/project.model';
import { TaskFilters, TaskUserRoleFilter } from '@app/shared/types/models/task/task-filters.model';
import { TaskListViewMode } from '@app/shared/types/models/task/task-schedule.model';
import { User } from '@app/shared/types/models/user/user.model';
import { PROFFEO_UI_TABLE_IDS } from '@app/shared/ui/table/ui-table-ids';
import { UiTablePreferencesService } from '@app/shared/ui/table/ui-table-preferences.service';
import { ProffeoUiSelectOption } from '@app/shared/ui/types/select.types';
import { proffeoBuildTree, ProffeoUiTreeNode } from '@app/shared/ui/types/tree.types';
import { normalizeTaskFilters } from '@app/shared/utils/task-filters.util';
import { isEqual } from 'lodash';
import { createTaskGroupExpansionState, parseTaskGrouping, TaskGrouping } from '../task-grouping.util';

const DEFAULT_ORDER_BY = TaskSortField.LastActivityAt;
const DEFAULT_ORDER = SortDirection.DESC;
const DEFAULT_LIMIT = 50;
const DEFAULT_PAGE = 0;

/** Compatibility alias for existing callers; grouping is shared by all task renderers. */
export type MobileTaskGrouping = TaskGrouping;
export type MobileTaskAvatarMode = 'none' | 'task' | 'participants';
export type MobileTaskDateMode = 'none' | 'start' | 'end';

/** PROPOZYCJA — DO WERYFIKACJI: shared presentation options for mobile grouping controls. */
export const MOBILE_TASK_GROUPING_OPTIONS: ProffeoUiSelectOption<MobileTaskGrouping>[] = [
  { value: 'none', label: 'proffeo-ui-table.group-none' },
  { value: 'status', label: 'task-filters.status' },
  { value: 'priority', label: 'task-list-group-priority' },
  { value: 'industry', label: 'task-filters.industry' },
  { value: 'location', label: 'task-list-group-location' }
];

/**
 * TaskFiltersService - Holds task list filter state, URL query sync, and available filter options.
 *
 * Centralizes pagination/sort params, saved filter id, in-memory filter criteria, and derived option lists
 * from project data services.
 *
 * Scope: Route-level instance via `providers` on the project tasks view (same injector as {@link TaskFiltersHelperService}).
 *
 * Usage: Toolbar and view components inject this service for all filter state reads and writes; {@link TasksListActionsService}
 * uses it for list queries. {@link TaskFiltersHelperService} reads it internally for labels/modals only.
 *
 * Architecture:
 * - {@link TaskFiltersService}: Mutable filter state, URL sync, option signals from data layer
 * - {@link TaskFiltersHelperService}: Presentation-only helpers (no state API surface)
 */
@Injectable()
export class TaskFiltersService {
  private readonly tagsDataService: TagsDataService = inject(TagsDataService);
  private readonly taskTypesDataService: TaskTypesDataService = inject(TaskTypesDataService);
  private readonly projectParticipantsDataService: ProjectParticipantsDataService =
    inject(ProjectParticipantsDataService);
  private readonly projectIndustriesDataService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly locationsDataService: LocationsDataService = inject(LocationsDataService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly authService: AuthService = inject(AuthService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly activeProject: Signal<Project> = this.activeProjectService.activeProject;
  private readonly projectId = computed(() => this.activeProject()?.id);
  private readonly _orderBy: WritableSignal<TaskSortField> = signal(DEFAULT_ORDER_BY);
  private readonly _order: WritableSignal<SortDirection> = signal(DEFAULT_ORDER);
  private readonly _limit: WritableSignal<number> = signal(DEFAULT_LIMIT);
  private readonly _page: WritableSignal<number> = signal(DEFAULT_PAGE);
  private readonly _taskFiltersId: WritableSignal<string | null> = signal<string | null>(null);
  private readonly _filters: WritableSignal<TaskFilters | null> = signal<TaskFilters | null>(null, {
    equal: (previous, current) => isEqual(previous, current)
  });

  private readonly taskGroupExpansion = createTaskGroupExpansionState();
  private readonly tablePreferencesService = inject(UiTablePreferencesService);

  /** Shared across list/detail child routes; switching a task preserves the selected renderer. */
  public readonly viewMode = signal<TaskListViewMode>('table');
  /**
   * View-only grouping shared by all task renderers (not an API filter). Starts from, and is saved
   * back to, the task table's saved `groupByColumnId` preference so the choice survives navigation.
   */
  public readonly taskGrouping = signal<TaskGrouping>(
    parseTaskGrouping(this.tablePreferencesService.preferences()[PROFFEO_UI_TABLE_IDS.tasksList]?.groupByColumnId)
  );
  public readonly mobileGrouping = this.taskGrouping;
  public readonly expandedTaskGroupIds = this.taskGroupExpansion.expandedTaskGroupIds;
  public readonly reconcileTaskGroups = this.taskGroupExpansion.reconcileTaskGroups;
  /** PROPOZYCJA — DO WERYFIKACJI: route-scoped presentation options for cards and compact. */
  public readonly mobileAvatarMode = signal<MobileTaskAvatarMode>('task');
  public readonly mobileDateMode = signal<MobileTaskDateMode>('none');
  public readonly mobileStatusVisible = signal(true);
  public readonly mobilePriorityVisible = signal(true);
  /** Keeps the latest non-empty choices when a whole view-settings group is turned off. */
  public readonly mobileLastAvatarMode = signal<Exclude<MobileTaskAvatarMode, 'none'>>('task');
  public readonly mobileLastDateMode = signal<Exclude<MobileTaskDateMode, 'none'>>('start');
  public readonly mobileLastTaskData = signal({ status: true, priority: true });

  public readonly availableSortByOptions: Signal<ProffeoUiSelectOption<TaskSortField>[]> = signal(
    [
      TaskSortField.CreatedAt,
      TaskSortField.LastActivityAt,
      TaskSortField.Id,
      TaskSortField.Name,
      TaskSortField.Type,
      TaskSortField.Status,
      TaskSortField.PercentageOfProgress,
      TaskSortField.Author
    ].map(sortField => ({
      value: sortField,
      label: TASK_SORT_FIELD_TRANSLATION_KEYS.get(sortField) ?? ''
    }))
  );

  public readonly availableStatusOptions: Signal<ProffeoUiSelectOption<TaskStatus>[]> = signal(
    Object.values(TaskStatus).map(status => ({
      value: status,
      label: TASK_STATUS_TRANSLATION_KEYS.get(status)
    }))
  );

  public readonly availablePriorityOptions: Signal<ProffeoUiSelectOption<TaskPriority>[]> = signal([
    {
      value: TaskPriority.NORMAL,
      label: TASK_PRIORITY_TRANSLATION_KEYS.get(TaskPriority.NORMAL)
    },
    {
      value: TaskPriority.MEDIUM,
      label: TASK_PRIORITY_TRANSLATION_KEYS.get(TaskPriority.MEDIUM)
    },
    {
      value: TaskPriority.HIGH,
      label: TASK_PRIORITY_TRANSLATION_KEYS.get(TaskPriority.HIGH)
    }
  ]);

  public readonly availableTagsOptions: Signal<ProffeoUiSelectOption<string>[]> = computed(() =>
    (this.tagsDataService.getProjectTags(this.projectId())() ?? []).map(tag => ({
      value: tag.id,
      label: tag.name,
      context: tag
    }))
  );
  public readonly availableTaskTypes: Signal<ProffeoUiSelectOption<string>[]> = computed(() => {
    const taskTypes = this.taskTypesDataService.getActiveCompanyTaskTypes()() ?? [];
    return taskTypes.map(taskType => ({
      value: taskType.id,
      label: taskType.name
    }));
  });

  public readonly availableIndustriesOptions: Signal<ProffeoUiSelectOption<string>[]> = computed(() => {
    const projectIndustries = this.projectIndustriesDataService.getProjectIndustries(this.projectId())() ?? [];
    return projectIndustries.map(projectIndustry => ({
      value: projectIndustry.industry().id,
      label: projectIndustry.industry().name
    }));
  });

  public readonly availableUsers: Signal<User[]> = computed(() => {
    const participants: ProjectParticipant[] =
      this.projectParticipantsDataService.getProjectParticipants(this.projectId())() ?? [];
    const participantsWithLoggedInUser = new Map<string, User>(
      participants.map(participant => [participant.user().id, participant.user()])
    );
    if (this.authService.currentUser() !== null && this.authService.currentUser() !== undefined) {
      participantsWithLoggedInUser.set(this.authService.currentUser().id, this.authService.currentUser());
    }
    const uniqueUsers = participantsWithLoggedInUser.values();
    return Array.from(uniqueUsers);
  });

  public readonly availableUsersOptions: Signal<ProffeoUiSelectOption<string>[]> = computed(() =>
    this.availableUsers().map(user => ({
      value: user.id,
      label: user.fullName
    }))
  );

  public readonly availableGroups: Signal<Group[]> = computed(
    () => this.projectParticipantsDataService.getProjectGroups(this.projectId())() ?? []
  );

  public readonly availableProjectParticipants: Signal<ProjectParticipant[]> = computed(
    () => this.projectParticipantsDataService.getProjectParticipants(this.projectId())() ?? []
  );

  public readonly availableGroupAdmins: Signal<User[]> = computed(() => {
    const participants = this.projectParticipantsDataService.getProjectParticipants(this.projectId())() ?? [];
    const adminUsers = participants
      .filter(participant => participant.role === GroupRole.ADMIN)
      .map(participant => participant.user());
    const uniqueUsers = new Map(adminUsers.map(user => [user.id, user])).values();
    return Array.from(uniqueUsers);
  });

  public readonly availableIndustryAdmins: Signal<User[]> = computed(() => {
    const projectIndustries = this.projectIndustriesDataService.getProjectIndustries(this.projectId())() ?? [];
    const admins = projectIndustries
      .map(projectIndustry => projectIndustry.administrator?.())
      .filter((administrator): administrator is User => administrator !== null && administrator !== undefined);
    const uniqueUsers = new Map(admins.map(user => [user.id, user])).values();
    return Array.from(uniqueUsers);
  });

  public readonly parentLocations: Signal<Location[]> = computed(
    () => this.locationsDataService.getProjectLocations(this.projectId())() ?? []
  );

  public readonly availableLocations: Signal<Location[]> = computed(() => {
    const parentLocations = this.parentLocations();
    const allSublocations = parentLocations.flatMap(location =>
      location?.id ? this.locationsDataService.getAllSubLocations(location.id) : []
    );
    return [...parentLocations, ...allSublocations];
  });

  public readonly locationTreeNodes: Signal<ProffeoUiTreeNode<string>[]> = computed(() =>
    proffeoBuildTree(this.availableLocations(), {
      getId: location => location.id ?? '',
      getParentId: location => location.parentLocation?.()?.id,
      getLabel: location => location.name ?? '',
      getValue: location => location.id ?? ''
    })
  );

  public readonly groupTreeNodes: Signal<ProffeoUiTreeNode<string>[]> = computed(() =>
    proffeoBuildTree(this.availableGroups(), {
      getId: group => group.id,
      getParentId: group => group.parentGroupId,
      getLabel: group => group.name,
      getValue: group => group.id
    })
  );

  public readonly orderBy: Signal<TaskSortField> = this._orderBy.asReadonly();
  public readonly order: Signal<SortDirection> = this._order.asReadonly();
  public readonly limit: Signal<number> = this._limit.asReadonly();
  public readonly page: Signal<number> = this._page.asReadonly();
  public readonly taskFiltersId: Signal<string | null> = this._taskFiltersId.asReadonly();
  public readonly searchPhrase: Signal<string | undefined> = computed(() => this._filters()?.text ?? undefined);

  public readonly filters: Signal<TaskFilters | null> = this._filters.asReadonly();

  public readonly isMyIndustryTasksFilterActive: Signal<boolean> = computed(() =>
    this.isCurrentUserRoleFilterActive(ProjectTaskUserRole.INDUSTRY_ADMIN)
  );

  public readonly isMyGroupTasksFilterActive: Signal<boolean> = computed(() =>
    this.isCurrentUserRoleFilterActive(ProjectTaskUserRole.GROUP_ADMIN)
  );

  public readonly isMyContractorTasksFilterActive: Signal<boolean> = computed(() =>
    this.isCurrentUserRoleFilterActive(ProjectTaskUserRole.USER)
  );

  public readonly myRolesActiveCount: Signal<number> = computed(() => {
    let count = 0;
    if (this.isMyContractorTasksFilterActive()) count += 1;
    if (this.isMyIndustryTasksFilterActive()) count += 1;
    if (this.isMyGroupTasksFilterActive()) count += 1;
    return count;
  });

  public readonly selectedMyRoles: Signal<ProjectTaskUserRole[]> = computed(() => {
    const roles: ProjectTaskUserRole[] = [];
    if (this.isMyContractorTasksFilterActive()) roles.push(ProjectTaskUserRole.USER);
    if (this.isMyIndustryTasksFilterActive()) roles.push(ProjectTaskUserRole.INDUSTRY_ADMIN);
    if (this.isMyGroupTasksFilterActive()) roles.push(ProjectTaskUserRole.GROUP_ADMIN);
    return roles;
  });

  public readonly areAllMyRolesSelected: Signal<boolean> = computed(() => this.myRolesActiveCount() === 3);

  public readonly areSomeMyRolesSelected: Signal<boolean> = computed(() => {
    const count = this.myRolesActiveCount();
    return count > 0 && count < 3;
  });

  public readonly activeFiltersCount: Signal<number> = computed(() => {
    const filters: TaskFilters | null = this._filters();
    let count = 0;
    if (filters?.text?.trim?.()) count += 1;
    if (!filters) return count;
    if (filters.taskStatus?.length) count += filters.taskStatus.length;
    if (filters.taskPriority?.length) count += filters.taskPriority.length;
    if (filters.group?.length) count += filters.group.length;
    if (filters.user?.length) count += filters.user.length;
    if (filters.location?.length) count += filters.location.length;
    if (filters.industry?.length) count += filters.industry.length;
    if (filters.userRole?.length) count += filters.userRole.length;
    if (filters.createdBy?.length) count += filters.createdBy.length;
    if (filters.projectTagIds?.length) count += filters.projectTagIds.length;
    if (filters.taskTypeIds?.length) count += filters.taskTypeIds.length;
    if (filters.startDateFrom != null || filters.startDateTo != null) count += 1;
    if (filters.endDateFrom != null || filters.endDateTo != null) count += 1;
    if (filters.createdDateFrom != null || filters.createdDateTo != null) count += 1;
    if (filters.editDateFrom != null || filters.editDateTo != null) count += 1;
    if (filters.noIndustry ?? false) count += 1;
    if (filters.noUsers ?? false) count += 1;
    if (filters.noLocation ?? false) count += 1;
    if (filters.noGroup ?? false) count += 1;
    return count;
  });

  /**
   * Active criteria available in the floating filter panel.
   * Excludes text search and current-user “my roles” entries, which remain toolbar-only controls.
   */
  public readonly advancedActiveFiltersCount: Signal<number> = computed(() => {
    const filters: TaskFilters | null = this._filters();
    if (!filters) return 0;
    let count = 0;
    if (filters.taskStatus?.length) count += filters.taskStatus.length;
    if (filters.taskPriority?.length) count += filters.taskPriority.length;
    if (filters.group?.length) count += filters.group.length;
    if (filters.user?.length) count += filters.user.length;
    if (filters.location?.length) count += filters.location.length;
    if (filters.industry?.length) count += filters.industry.length;
    const advancedUserRoleCount = Math.max(0, (filters.userRole?.length ?? 0) - this.myRolesActiveCount());
    if (advancedUserRoleCount) count += advancedUserRoleCount;
    if (filters.createdBy?.length) count += filters.createdBy.length;
    if (filters.projectTagIds?.length) count += filters.projectTagIds.length;
    if (filters.taskTypeIds?.length) count += filters.taskTypeIds.length;
    if (filters.startDateFrom != null || filters.startDateTo != null) count += 1;
    if (filters.endDateFrom != null || filters.endDateTo != null) count += 1;
    if (filters.createdDateFrom != null || filters.createdDateTo != null) count += 1;
    if (filters.editDateFrom != null || filters.editDateTo != null) count += 1;
    if (filters.noIndustry ?? false) count += 1;
    if (filters.noUsers ?? false) count += 1;
    if (filters.noLocation ?? false) count += 1;
    if (filters.noGroup ?? false) count += 1;
    return count;
  });

  public constructor() {
    this.subscribeToFiltersFromUrl();
    this.updateUrl();
    this.persistTaskGrouping();
  }

  /** Saves grouping chosen in any renderer (table, cards, compact, mobile) to the task table preference. */
  private persistTaskGrouping(): void {
    let savedGrouping = this.taskGrouping();
    effect(() => {
      const grouping = this.taskGrouping();
      // Only real changes are written; the restored value is never echoed back.
      if (grouping === savedGrouping) {
        return;
      }
      savedGrouping = grouping;
      untracked(() =>
        this.tablePreferencesService.setGroupByColumnId(
          PROFFEO_UI_TABLE_IDS.tasksList,
          grouping === 'none' ? null : grouping
        )
      );
    });
  }

  private subscribeToFiltersFromUrl(): void {
    this.activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(queryParams => {
      const orderFromUrl = queryParams.get('order');
      const orderByFromUrl = queryParams.get('orderBy');
      const limitFromUrl = queryParams.get('limit');
      const pageFromUrl = queryParams.get('page');
      if (orderFromUrl) {
        const parsedOrder = Object.values(SortDirection).find(
          enumValue => enumValue.toLowerCase() === orderFromUrl.toLowerCase()
        );
        if (parsedOrder && this._order() !== parsedOrder) this._order.set(parsedOrder);
      }
      if (orderByFromUrl) {
        const parsedOrderBy = Object.values(TaskSortField).find(
          enumValue => enumValue.toLowerCase() === orderByFromUrl.toLowerCase()
        );
        if (parsedOrderBy && this._orderBy() !== parsedOrderBy) this._orderBy.set(parsedOrderBy);
      }
      if (limitFromUrl) {
        const limitNumber = +limitFromUrl;
        if (!isNaN(limitNumber) && this._limit() !== limitNumber) this._limit.set(limitNumber);
      }
      if (pageFromUrl) {
        const pageNumber = +pageFromUrl;
        if (!isNaN(pageNumber) && this._page() !== pageNumber) this._page.set(pageNumber);
      }
      const taskFiltersIdFromUrl = queryParams.get('taskFiltersId');
      if (this._taskFiltersId() !== (taskFiltersIdFromUrl ?? null)) {
        this._taskFiltersId.set(taskFiltersIdFromUrl ?? null);
      }
    });
  }

  public setOrderBy(value: TaskSortField | null): void {
    this._orderBy.set(value ?? DEFAULT_ORDER_BY);
    this.updateUrl();
  }

  public setOrder(value: SortDirection): void {
    this._order.set(value);
    this.updateUrl();
  }

  public setSort(orderBy: TaskSortField, order: SortDirection): void {
    this._orderBy.set(orderBy);
    this._order.set(order);
    this.updateUrl();
  }

  public resetSort(): void {
    this.setSort(DEFAULT_ORDER_BY, DEFAULT_ORDER);
  }

  public setLimit(value: number): void {
    this._limit.set(value);
    this.updateUrl();
  }

  public setPage(value: number): void {
    this._page.set(value);
    this.updateUrl();
  }

  public setTaskFiltersId(value: string | null): void {
    this._taskFiltersId.set(value);
    this.updateUrl();
  }

  public setSearchPhrase(value: string | null): void {
    const trimmed = value?.trim() ?? undefined;
    this._filters.update(filters => ({ ...filters, text: trimmed }));
    this._page.set(0);
    this.updateUrl();
  }

  public setFiltersFromResponse(filtersId?: string | null, filters?: TaskFilters | null): void {
    if (filtersId !== undefined) this._taskFiltersId.set(filtersId ?? null);
    if (filters !== undefined) this._filters.set(normalizeTaskFilters(filters));
    this.updateUrl();
  }

  public clearFilters(): void {
    this._orderBy.set(DEFAULT_ORDER_BY);
    this._order.set(DEFAULT_ORDER);
    this._limit.set(DEFAULT_LIMIT);
    this._page.set(DEFAULT_PAGE);
    this._taskFiltersId.set(null);
    this._filters.set({});
    this.updateUrl();
  }

  private updateUrl(): void {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    const currentOrderInUrl = queryParams.get('order');
    const currentOrderByInUrl = queryParams.get('orderBy');
    const currentFiltersIdInUrl = queryParams.get('taskFiltersId');
    const currentPageInUrl = queryParams.get('page');
    const currentLimitInUrl = queryParams.get('limit');

    if (
      currentOrderInUrl === this._order() &&
      currentOrderByInUrl === this._orderBy() &&
      currentFiltersIdInUrl === (this._taskFiltersId() ?? null) &&
      Number(currentPageInUrl) === this._page() &&
      Number(currentLimitInUrl) === this._limit()
    ) {
      return;
    }

    const scrollContainer = document.querySelector('.app-content');
    const scrollTop = scrollContainer instanceof HTMLElement ? scrollContainer.scrollTop : 0;

    void this.router
      .navigate([], {
        queryParams: {
          taskFiltersId: this._taskFiltersId() ?? undefined,
          orderBy: this._orderBy(),
          order: this._order(),
          limit: this._limit(),
          page: this._page()
        },
        queryParamsHandling: 'merge'
      })
      .then(() => {
        this.restoreAppContentScroll(scrollContainer, scrollTop);
      });
  }

  private restoreAppContentScroll(scrollContainer: Element | null, scrollTop: number): void {
    if (!(scrollContainer instanceof HTMLElement)) {
      return;
    }

    requestAnimationFrame(() => {
      scrollContainer.scrollTop = scrollTop;
    });
  }

  public setCurrentUserRoleFilter(role: ProjectTaskUserRole, checked: boolean): void {
    const currentUserId = this.currentUserId();
    if (currentUserId == null) {
      return;
    }

    const filters = this.cloneFilters();
    const remainingRoles: TaskUserRoleFilter[] = (filters.userRole ?? []).filter(
      userRoleFilter => userRoleFilter.role !== role
    );

    if (checked) {
      remainingRoles.push({ role, applicationUserId: currentUserId });
    }

    filters.userRole = remainingRoles;
    this.applyFilters(this.areFiltersEmpty(filters) ? null : filters);
  }

  public setAllMyRolesFilters(checked: boolean): void {
    this.setMyRolesFilters(
      checked ? [ProjectTaskUserRole.USER, ProjectTaskUserRole.INDUSTRY_ADMIN, ProjectTaskUserRole.GROUP_ADMIN] : []
    );
  }

  /** Replaces current-user role filters with the given set; preserves other users' role filters. */
  public setMyRolesFilters(roles: ProjectTaskUserRole[]): void {
    const currentUserId = this.currentUserId();
    if (currentUserId == null) {
      return;
    }

    const filters = this.cloneFilters();
    const otherUserRoles: TaskUserRoleFilter[] = (filters.userRole ?? []).filter(
      userRoleFilter => userRoleFilter.applicationUserId !== currentUserId
    );
    const uniqueRoles = [...new Set(roles)];

    filters.userRole = [...otherUserRoles, ...uniqueRoles.map(role => ({ role, applicationUserId: currentUserId }))];

    this.applyFilters(this.areFiltersEmpty(filters) ? null : filters);
  }

  public updateFiltersField(fieldName: keyof TaskFilters, value: unknown): void {
    const current = this.filters();
    let filters: TaskFilters | null = current ? structuredClone(current) : {};

    (filters as Record<string, unknown>)[fieldName] = value;
    if (this.areFiltersEmpty(filters)) filters = null;
    this.applyFilters(filters);
  }

  /** Updates both bounds of a date-range filter in a single store / URL sync. */
  public updateDateRangeFilter(
    fromField: keyof TaskFilters,
    toField: keyof TaskFilters,
    start: Date | null,
    end: Date | null
  ): void {
    const current = this.filters();
    let filters: TaskFilters | null = current ? structuredClone(current) : {};
    const record = filters as Record<string, unknown>;

    if (start == null) {
      delete record[fromField as string];
    } else {
      record[fromField as string] = start;
    }

    if (end == null) {
      delete record[toField as string];
    } else {
      record[toField as string] = end;
    }

    if (this.areFiltersEmpty(filters)) filters = null;
    this.applyFilters(filters);
  }

  /** Clears both bounds of a date-range filter in one update (URL / store). */
  public clearDateRangeFilter(fromField: keyof TaskFilters, toField: keyof TaskFilters): void {
    const current = this.filters();
    let filters: TaskFilters | null = current ? structuredClone(current) : {};
    const record = filters as Record<string, unknown>;
    delete record[fromField as string];
    delete record[toField as string];
    if (this.areFiltersEmpty(filters)) filters = null;
    this.applyFilters(filters);
  }

  public applyFilters(filters: TaskFilters | null): void {
    this._filters.set(filters);
    if (filters === null || filters === undefined) {
      this._taskFiltersId.set(null);
    }
    this._page.set(0);
    this.updateUrl();
  }

  private currentUserId(): string | undefined {
    return this.authService.currentUser()?.id;
  }

  private isCurrentUserRoleFilterActive(role: ProjectTaskUserRole): boolean {
    const currentUserId = this.currentUserId();
    if (currentUserId == null) {
      return false;
    }

    return (this._filters()?.userRole ?? []).some(
      userRoleFilter => userRoleFilter.role === role && userRoleFilter.applicationUserId === currentUserId
    );
  }

  private cloneFilters(): TaskFilters {
    const current = this.filters();
    return current ? structuredClone(current) : {};
  }

  private areFiltersEmpty(filters: TaskFilters): boolean {
    let filled = false;
    if (!filters) return true;
    Object.values(filters).forEach(filterValue => {
      if (filterValue === null || filterValue === undefined) return;
      if (typeof filterValue === 'boolean' && filterValue === true) {
        filled = true;
      } else if (filterValue?.length > 0) {
        filled = true;
      } else if (typeof filterValue === 'string' && filterValue === '') {
        filled = true;
      } else if (typeof filterValue === 'object' && filterValue instanceof Date) {
        filled = true;
      }
    });
    return !filled;
  }
}
